import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { personalInfoService, PersonalInfo } from '../../../../services/personalInfo';
import { tasksService } from '../../../../services/tasks';
import { mapTaskIdToKey } from '../../../../utils/taskKey';

import { ms, s, vs } from '../../../../utils/responsive';
import { createLogger } from '../../../../utils/logger';
const logger = createLogger('PersonalInfoLockUnlock');

type RouteParams = {
  taskId: number;
};

export const PersonalInfoLockUnlockScreen: React.FC = () => {
  const navigation = useNavigation();
  const route = useRoute<RouteProp<Record<string, RouteParams>, string>>();
  const taskId = route.params?.taskId || 1;

  const [personalInfo, setPersonalInfo] = useState<PersonalInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadPersonalInfo();
  }, [taskId]);

  const loadPersonalInfo = async () => {
    try {
      const data = await personalInfoService.getPersonalInfo(taskId);
      if (data && data.length > 0) {
        setPersonalInfo(data[0]);
      }
    } catch (error) {
      logger.error('Error loading personal info:', error);
      Alert.alert('Error', 'Failed to load personal information.');
    } finally {
      setLoading(false);
    }
  };

  const getTaskTitle = (id: number) => {
    switch (id) {
      case 1: return 'Identifying Information Completed';
      case 2: return 'Employment Information Completed';
      case 3: return 'Spouse Information Completed';
      case 4: return 'Children Information Completed';
      case 5: return 'Pet Information Completed';
      default: return 'Personal Information Completed';
    }
  };

  const handleLockPress = async () => {
    if (!personalInfo) return;

    try {
      setActionLoading(true);

      // Lock the personal info
      await personalInfoService.lockPersonalInfo(personalInfo.id);

      // Mark task as completed using the proper task key
      const taskKey = mapTaskIdToKey(taskId);
      await tasksService.completeTask(taskKey);

      (navigation as any).navigate('PersonalInfoLocked', { taskId });
    } catch (error) {
      logger.error('Error locking personal info:', error);
      Alert.alert('Error', 'Failed to lock personal information. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnlockPress = async () => {
    if (!personalInfo) {
      Alert.alert('Error', 'Missing personal information ID');
      return;
    }

    try {
      setActionLoading(true);

      // Mark task as completed using the proper task key
      const taskKey = mapTaskIdToKey(taskId);
      await tasksService.completeTask(taskKey);

      (navigation as any).navigate('PersonalInfoUnlocked', { personalInfoId: personalInfo.id });
    } catch (error) {
      logger.error('Error completing task:', error);
      // Still navigate even if task completion fails
      (navigation as any).navigate('PersonalInfoUnlocked', { personalInfoId: personalInfo.id });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
          <ActivityIndicator size="large" color="#1C2C4F" />
          <Text style={[styles.title, { marginTop: 20 }]}>Loading...</Text>
        </View>
      </SafeAreaView>
    );
  }
  
  if (!personalInfo) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
          <Text style={styles.title}>No personal information found</Text>
          <TouchableOpacity 
            style={[styles.button, styles.unlockButton, { marginTop: 20 }]}
            onPress={() => navigation.goBack()}
          >
            <Text style={styles.buttonText}>Go Back</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
      </View>

      <View style={styles.content}>
        <Text style={styles.title}>{getTaskTitle(taskId)}</Text>


        <View style={styles.blueContainer}>
          <Text style={styles.messageText}>
            You're all set! Your {getTaskTitle(taskId).toLowerCase().replace(' completed', '')} has been successfully entered! Would you like to share this information with your successor or Key Holders?
          </Text>
        </View>

        <View style={styles.grayButtonContainer}>
          <TouchableOpacity 
            style={[styles.button, styles.lockButton, actionLoading && { opacity: 0.7 }]} 
            onPress={handleLockPress}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <ActivityIndicator color="#FFFFFF" size="small" />
            ) : (
              <Image
                source={require('../../../../assets/icons/BlueLock.png')}
                style={styles.buttonIcon}
              />
            )}
            <Text style={[styles.buttonText, styles.lockText]}>Keep it locked!</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.button,
              styles.unlockButton,
              actionLoading && { opacity: 0.5 }
            ]}
            onPress={handleUnlockPress}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <ActivityIndicator color="#FFFFFF" size="small" />
            ) : (
              <Image
                source={require('../../../../assets/icons/BlueUnlock.png')}
                style={styles.buttonIcon}
              />
            )}
            <Text style={[styles.buttonText, styles.unlockText]}>
              I Want to Unlock it!
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(15),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: ms(32, 0.3),
    height: ms(32, 0.3),
    tintColor: '#333',
  },
  logo: {
    width: ms(80, 0.3),
    height: ms(80, 0.3),
  },
  profileContainer: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    width: ms(45, 0.3),
    height: ms(45, 0.3),
    borderRadius: ms(22.5),
    backgroundColor: '#fff',
    padding: ms(2),
  },
  profileImage: {
    width: '100%',
    height: '100%',
    borderRadius: ms(21),
  },
  content: {
    flex: 1,
    justifyContent: 'space-between',
  },
  title: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    textAlign: 'center',
    marginTop: ms(20),
    paddingHorizontal: ms(20),
  },
  subtitle: {
    fontSize: ms(24, 0.3),
    color: '#1C2C4F',
    textAlign: 'center',
    marginTop: ms(5),
  },
  blueContainer: {
    backgroundColor: '#1C2C4F',
    padding: ms(20),
    paddingBottom: ms(180),
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    position: 'absolute',
    bottom: ms(120),
    left: 0,
    right: 0,
    zIndex: 1,
  },
  messageText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    lineHeight: ms(30, 0.3),
    textAlign: 'center',
    paddingHorizontal: ms(25),
  },
  grayButtonContainer: {
    backgroundColor: '#F3F6F8',
    padding: ms(20),
    paddingTop: ms(30),
    paddingBottom: ms(60),
    borderRadius: ms(30),
    gap: ms(25),
    position: 'absolute',
    bottom: ms(20),
    left: 0,
    right: 0,
    zIndex: 2,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: ms(10),
    paddingLeft: ms(25),
    borderRadius: ms(50),
    gap: ms(50),
  },
  buttonIcon: {
    width: ms(50, 0.3),
    height: ms(50, 0.3),
    resizeMode: 'contain',
  },
  buttonText: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    alignSelf: 'center',
  },
  lockButton: {
    backgroundColor: '#FF9F5A',
  },
  unlockButton: {
    backgroundColor: '#29BFC0',
  },
  lockText: {
    color: '#FFFFFF',
  },
  unlockText: {
    color: '#FFFFFF',
  },
}); 